import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Cookies from "js-cookie";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    IconButton,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    ArrowBack,
    BusinessOutlined,
    ChatOutlined,
    ContentCopyOutlined,
    DeleteOutline,
    EditOutlined,
    EmailOutlined,
    LocationOnOutlined,
    OpenInNew,
    PersonOutlined,
    PhoneOutlined,
    SmartToy,
} from "@mui/icons-material";
import { WB_SX, WB_UI } from "../../../ui/ux/uiTokens";
import CRMAvatar from "../common/CRMAvatar";
import CRMBadge from "../common/CRMBadge";
import axiosInstance from "../../../api/axiosInstance";
import base_url from "../../../constants";
import useNotification from "../../common/Notification";
import { DeleteConfirmDialog, EditContactDialog } from "./CRMContacts";

const TAG_COLOR_PALETTE = [
    { bg: "#fef3c7", text: "#92400e" },
    { bg: "#e0e7ff", text: "#3730a3" },
    { bg: "#d1fae5", text: "#065f46" },
    { bg: "#fce7f3", text: "#9d174d" },
    { bg: "#dbeafe", text: "#1d4ed8" },
    { bg: "#ffe4e6", text: "#be123c" },
];

const CONTACT_STATUS = {
    interested:     { bg: "#f0fdf4", text: "#166534", label: "Interested" },
    not_interested: { bg: "#fef2f2", text: "#991b1b", label: "Not Interested" },
};

const DEAL_STATUS = {
    open: { bg: "#eff6ff", text: "#1d4ed8", label: "Open" },
    won:  { bg: "#d1fae5", text: "#065f46", label: "Won" },
    lost: { bg: "#fee2e2", text: "#b91c1c", label: "Lost" },
};

const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/);
    return parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : name.slice(0, 2).toUpperCase();
};

const fmtDate = (d) => {
    if (!d) return "—";
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

const fmtMoney = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n) || n === 0) return "—";
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);
};

const DetailRow = ({ icon, label, value, copy, onCopy }) => {
    if (!value) return null;
    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(String(value));
            onCopy?.(`${label} copied`, "success");
        } catch {
            onCopy?.(`Could not copy ${label.toLowerCase()}`, "error");
        }
    };
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                py: 0.85,
                borderBottom: "1px solid rgba(0,0,0,0.05)",
                "&:last-child": { borderBottom: "none" },
            }}
        >
            <Box sx={{ color: "text.disabled", display: "flex", flexShrink: 0 }}>{icon}</Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" color="text.disabled" sx={{ display: "block", lineHeight: 1.2 }}>
                    {label}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-word" }}>
                    {value}
                </Typography>
            </Box>
            {copy && (
                <Tooltip title="Copy">
                    <IconButton
                        size="small"
                        sx={{ color: "text.disabled" }}
                        onClick={handleCopy}
                    >
                        <ContentCopyOutlined sx={{ fontSize: 14 }} />
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    );
};

const CRMContactDetail = () => {
    const { contactId } = useParams();
    const navigate = useNavigate();
    const _notify = useNotification();
    const notifyRef = useRef(_notify);
    notifyRef.current = _notify;
    const showCrmBotControls = (Cookies.get("userRole") || "").split(",").map((r) => r.trim()).includes("WB-AGENT");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [contact, setContact] = useState(null);
    const [deals, setDeals] = useState([]);
    const [availableTags, setAvailableTags] = useState([]);
    const [editOpen, setEditOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const load = useCallback(async () => {
        if (!contactId) return;
        setLoading(true);
        setError("");
        try {
            const res = await axiosInstance.get(`${base_url}/crm/contacts/${contactId}`);
            const payload = res?.data?.data ?? res?.data ?? {};
            const nextContact = payload.contact ?? payload;
            const nextDeals = Array.isArray(payload.deals)
                ? payload.deals
                : Array.isArray(nextContact?.deals)
                    ? nextContact.deals
                    : [];

            setContact(nextContact && nextContact.id != null ? nextContact : null);
            setDeals(nextDeals);

            if (!nextContact || nextContact.id == null) {
                setError("Contact not found.");
            }
        } catch (err) {
            setContact(null);
            setDeals([]);
            setError(err?.response?.data?.message || "Could not load this contact.");
        } finally {
            setLoading(false);
        }
    }, [contactId]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        const fetchTags = async () => {
            try {
                const res = await axiosInstance.get(`${base_url}/crm/contact-tags`);
                setAvailableTags(Array.isArray(res.data?.data) ? res.data.data : []);
            } catch {
                setAvailableTags([]);
            }
        };
        fetchTags();
    }, []);

    const confirmDelete = async () => {
        if (!contact) return;
        const cid = contact.id ?? contact.contact_id;
        setDeleting(true);
        try {
            await axiosInstance.delete(`${base_url}/crm/contacts/${cid}`);
            notifyRef.current("Contact deleted successfully", "success");
            setDeleteOpen(false);
            navigate("/crm/contacts");
        } catch (err) {
            notifyRef.current(err?.response?.data?.message || "Failed to delete contact", "error");
        } finally {
            setDeleting(false);
        }
    };

    const openDeal = (deal) => {
        const dealId = deal?.id ?? deal?.deal_id;
        if (!dealId) return;
        window.open(`/crm/pipeline/${dealId}`, "_blank", "noopener,noreferrer");
    };

    if (loading) {
        return (
            <Box sx={{ ...WB_SX.page, display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress size={32} />
            </Box>
        );
    }

    if (error || !contact) {
        return (
            <Box sx={WB_SX.page}>
                <Button
                    startIcon={<ArrowBack />}
                    onClick={() => navigate("/crm/contacts")}
                    sx={{ textTransform: "none", mb: 2 }}
                >
                    Back to contacts
                </Button>
                <Alert severity="error">{error || "Contact not found."}</Alert>
            </Box>
        );
    }

    const statusMeta = CONTACT_STATUS[contact.status] || null;
    const tags = Array.isArray(contact.tags) ? contact.tags : [];

    return (
        <Box sx={WB_SX.page}>
            <Box sx={{ ...WB_SX.headerRow, alignItems: "flex-start" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <IconButton onClick={() => navigate("/crm/contacts")} sx={{ border: "1px solid #e5e7eb" }}>
                        <ArrowBack fontSize="small" />
                    </IconButton>
                    <CRMAvatar initials={getInitials(contact.name)} size={44} />
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ ...WB_SX.pageTitle, mb: 0.5 }} noWrap>
                            {contact.name || "Unnamed contact"}
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: -1 }}>
                            {statusMeta && (
                                <CRMBadge label={statusMeta.label} bg={statusMeta.bg} text={statusMeta.text} />
                            )}
                            {contact.source && <CRMBadge label={contact.source} />}
                            <CRMBadge
                                label={contact.is_bot_enabled ? "Bot reply on" : "Bot reply off"}
                                bg={contact.is_bot_enabled ? "#ecfdf5" : "#f3f4f6"}
                                text={contact.is_bot_enabled ? "#047857" : "#6b7280"}
                            />
                        </Box>
                    </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
                    <Tooltip title="Edit contact">
                        <IconButton
                            size="small"
                            onClick={() => setEditOpen(true)}
                            sx={{ color: "text.secondary", border: "1px solid #e5e7eb", "&:hover": { color: "#3b82f6" } }}
                        >
                            <EditOutlined sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={contact.phone ? "Open chat" : "No phone number"}>
                        <span>
                            <IconButton
                                size="small"
                                disabled={!contact.phone}
                                onClick={() => window.open(
                                    `/whatsapp-business/chat-window/contact/${contact.phone}`,
                                    "_blank",
                                    "noopener,noreferrer"
                                )}
                                sx={{ color: "text.secondary", border: "1px solid #e5e7eb", "&:hover": { color: "#25d366" } }}
                            >
                                <ChatOutlined sx={{ fontSize: 18 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Delete contact">
                        <IconButton
                            size="small"
                            onClick={() => setDeleteOpen(true)}
                            sx={{ color: "text.disabled", border: "1px solid #e5e7eb", "&:hover": { color: "error.main" } }}
                        >
                            <DeleteOutline sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            <Grid container spacing={2.5}>
                <Grid item xs={12} md={4}>
                    <Card elevation={0} sx={{ ...WB_SX.card, height: "100%" }}>
                        <CardContent>
                            <Typography sx={{ ...WB_SX.sectionTitle, mb: 1 }}>Contact details</Typography>
                            <DetailRow icon={<PhoneOutlined sx={{ fontSize: 16 }} />} label="Phone" value={contact.phone} copy onCopy={notifyRef.current} />
                            <DetailRow icon={<PhoneOutlined sx={{ fontSize: 16 }} />} label="Alternate phone" value={contact.alternate_contact_number} copy onCopy={notifyRef.current} />
                            <DetailRow icon={<EmailOutlined sx={{ fontSize: 16 }} />} label="Email" value={contact.email} copy onCopy={notifyRef.current} />
                            <DetailRow icon={<BusinessOutlined sx={{ fontSize: 16 }} />} label="Company" value={contact.company_name} />
                            <DetailRow icon={<LocationOnOutlined sx={{ fontSize: 16 }} />} label="Location" value={contact.location} />
                            <DetailRow icon={<PersonOutlined sx={{ fontSize: 16 }} />} label="Source" value={contact.source} />
                            <DetailRow icon={<SmartToy sx={{ fontSize: 16 }} />} label="Added" value={fmtDate(contact.created_at)} />
                            <Box sx={{ pt: 1.25 }}>
                                <Typography variant="caption" color="text.disabled" sx={{ display: "block", mb: 0.75 }}>
                                    Tags
                                </Typography>
                                {tags.length === 0 ? (
                                    <Typography variant="body2" color="text.disabled">No tags</Typography>
                                ) : (
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                        {tags.map((tag, index) => {
                                            const color = TAG_COLOR_PALETTE[index % TAG_COLOR_PALETTE.length];
                                            return (
                                                <CRMBadge
                                                    key={tag.id ?? index}
                                                    label={tag.tag_name}
                                                    bg={color.bg}
                                                    text={color.text}
                                                />
                                            );
                                        })}
                                    </Box>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={8}>
                    <Card elevation={0} sx={WB_SX.card}>
                        <CardContent>
                            <Typography sx={{ ...WB_SX.sectionTitle, mb: 1.5 }}>
                                Deals ({deals.length})
                            </Typography>
                            {deals.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    No deals for this contact yet.
                                </Typography>
                            ) : (
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                                    {deals.map((deal) => {
                                        const dealId = deal.id ?? deal.deal_id;
                                        const status = DEAL_STATUS[deal.status] || DEAL_STATUS.open;
                                        const stageName = deal.stage?.stage_name
                                            || deal.pipeline_stage?.stage_name
                                            || "";
                                        const assignee = deal.current_assigned_user?.name
                                            || deal.assigned_user?.name
                                            || deal.assignedUser?.name
                                            || "";
                                        return (
                                            <Box
                                                key={dealId}
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                    gap: 1.5,
                                                    flexWrap: "wrap",
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    border: `1px solid ${WB_UI.color.border}`,
                                                    backgroundColor: "#fafafa",
                                                }}
                                            >
                                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                        {deal.title || `Deal #${dealId}`}
                                                    </Typography>
                                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.75, alignItems: "center" }}>
                                                        <CRMBadge label={status.label} bg={status.bg} text={status.text} />
                                                        {stageName && <CRMBadge label={stageName} />}
                                                        <Typography variant="caption" color="text.secondary">
                                                            {fmtMoney(deal.value)}
                                                            {deal.expected_close_date ? ` · Close ${fmtDate(deal.expected_close_date)}` : ""}
                                                            {assignee ? ` · ${assignee}` : ""}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    endIcon={<OpenInNew sx={{ fontSize: 14 }} />}
                                                    onClick={() => openDeal(deal)}
                                                    sx={{ ...WB_SX.primaryButton, textTransform: "none" }}
                                                >
                                                    Open deal
                                                </Button>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <EditContactDialog
                open={editOpen}
                contact={contact}
                onClose={() => setEditOpen(false)}
                onUpdated={load}
                showCrmBotControls={showCrmBotControls}
                availableTags={availableTags}
            />
            <DeleteConfirmDialog
                open={deleteOpen}
                contact={contact}
                onClose={() => !deleting && setDeleteOpen(false)}
                onConfirm={confirmDelete}
                deleting={deleting}
            />
        </Box>
    );
};

export default CRMContactDetail;
